import { forwardRef, memo, useMemo } from "react";
import { Link } from "react-router-dom";
import {
	BarChart2,
	Gauge,
	Layers,
	ListChecks,
	MessagesSquare,
	PanelLeftClose,
	PanelLeftOpen,
	Plus,
	X,
} from "lucide-react";
import { RunStatusDot } from "@/components/atoms/testing/ActivityDot";
import { PanelShimmer } from "./PanelShimmer";
import { CoverageDashboardDialog } from "./CoverageDashboardDialog";
import { SuitesDialog } from "./SuitesDialog";
import {
	bucketByRecency,
	type SessionDot,
	type TestingSession,
	type TestingSessionGroup,
} from "@/utils/testing/sessions";
import { relativeTime } from "@/utils/testing/planDisplay";

/**
 * The LEFT pane: the plans of the conversation that is open — its table of contents.
 *
 * This used to list every plan across every conversation as if each were a separate "session", so two
 * chats holding 1 and 4 plans read as five unrelated rows. A conversation (session) now lives in the
 * History pane on the right; this pane shows only the plans of whichever one is open, and clicking a
 * plan scrolls the chat straight to it.
 */
export interface TestingPlanListProps {
	/** Plans of the active conversation, oldest → newest. */
	plans: TestingSession[];
	/** The open conversation's title, shown as the pane's subject. Null when nothing is open. */
	sessionTitle: string | null;
	/** The plan currently selected, highlighted and scrolled-to. */
	activePlanId: string | null;
	/** True while the active conversation is a draft that has not proposed a plan yet. */
	draft: boolean;
	loading: boolean;
	onSelectPlan: (plan: TestingSession) => void;
	onNew: () => void;
	onCollapse: () => void;
	/** Rendered as an overlay drawer, so it needs its own dismiss control. */
	asDrawer?: boolean;
	/** Seeds the chat composer — used by the Coverage dialog's "test this" actions. */
	onUseInChat: (text: string) => void;
}

export const TestingPlanList = memo(function TestingPlanList({
	plans,
	sessionTitle,
	activePlanId,
	draft,
	loading,
	onSelectPlan,
	onNew,
	onCollapse,
	asDrawer,
	onUseInChat,
}: TestingPlanListProps) {
	return (
		<div className="tst-panel flex h-full min-h-0 flex-col overflow-hidden border-r">
			{/* Header */}
			<div className="tst-toolbar flex shrink-0 items-center gap-1 px-2">
				<span className="tst-micro flex-1 truncate pl-1">
					Plans{plans.length > 0 ? ` · ${plans.length}` : ""}
				</span>
				<button
					type="button"
					onClick={onNew}
					className="tst-interactive inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
					title="Start a new test (⌘⇧N)"
				>
					<Plus className="h-3.5 w-3.5" />
					New
				</button>
				<button
					type="button"
					onClick={onCollapse}
					className="tst-interactive flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
					title={asDrawer ? "Close plans (Esc)" : "Collapse plans (⌘B)"}
					aria-label={asDrawer ? "Close plans" : "Collapse plans"}
				>
					{asDrawer ? <X className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
				</button>
			</div>

			{/* The open conversation's title — the subject these plans belong to. */}
			{sessionTitle && (
				<div className="shrink-0 border-b border-border/40 px-3 py-2">
					<p className="truncate text-[12px] font-medium text-foreground/90" title={sessionTitle}>
						{sessionTitle}
					</p>
					<p className="tst-micro mt-0.5">This session</p>
				</div>
			)}

			{/* Rows */}
			<div className="min-h-0 flex-1 overflow-y-auto py-1">
				{loading && plans.length === 0 && (
					<div className="p-2">
						<PanelShimmer rows={3} compact />
					</div>
				)}

				{!loading && plans.length === 0 && (
					<div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
						<ListChecks className="h-6 w-6 text-muted-foreground/40" />
						<p className="text-[11px] leading-relaxed text-muted-foreground">
							{draft
								? "No plan yet — describe this test in the chat and it appears here."
								: "Open a session from History, or start a new test."}
						</p>
					</div>
				)}

				<ul className="flex flex-col">
					{plans.map((plan) => (
						<PlanRow
							key={plan.id}
							plan={plan}
							active={plan.id === activePlanId}
							onSelect={() => onSelectPlan(plan)}
						/>
					))}
				</ul>
			</div>

			{/* Footer — the module's other entry points, always reachable. */}
			<div className="shrink-0 border-t border-border/60 p-1.5">
				<SuitesDialog trigger={<FooterAction icon={<Layers className="h-3.5 w-3.5" />} label="Regression suites" />} />
				<CoverageDashboardDialog
					onUseInChat={onUseInChat}
					trigger={<FooterAction icon={<Gauge className="h-3.5 w-3.5" />} label="Coverage" />}
				/>
				{/* A react-router Link, not an <a href>: a full document load wipes the in-memory testing
				    store, i.e. every open chat thread. */}
				<Link
					to="/testing/analytics"
					className="tst-interactive flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
				>
					<BarChart2 className="h-3.5 w-3.5" />
					Analytics
				</Link>
			</div>
		</div>
	);
});

/**
 * The shared shell for a sidebar row: a status dot, a title, an accented subtitle and a relative time,
 * in an active-aware button. A plan row and a conversation row differ only in what fills those slots.
 */
function SidebarRow({
	dot,
	title,
	subtitle,
	subtitleAccent,
	updatedAt,
	active,
	onSelect,
}: {
	dot: SessionDot;
	title: string;
	subtitle: string;
	/** Tints the subtitle — used for the live "Running now" state. */
	subtitleAccent?: boolean;
	updatedAt: number;
	active: boolean;
	onSelect: () => void;
}) {
	return (
		<li>
			<button
				type="button"
				onClick={onSelect}
				aria-current={active ? "true" : undefined}
				className={`tst-interactive flex w-full items-start gap-2 px-2.5 py-2 text-left ${
					active ? "bg-primary/10" : "hover:bg-muted/60"
				}`}
			>
				<RunStatusDot status={dot} className="mt-1.5" />
				<span className="flex min-w-0 flex-1 flex-col gap-0.5">
					<span
						className={`truncate text-[13px] leading-tight ${
							active ? "font-semibold text-foreground" : "font-medium text-foreground/90"
						}`}
					>
						{title}
					</span>
					<span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
						<span className={`truncate ${subtitleAccent ? "text-primary" : ""}`}>{subtitle}</span>
						{updatedAt > 0 && (
							<>
								<span aria-hidden className="opacity-40">
									·
								</span>
								<span className="shrink-0 tabular-nums">
									{relativeTime(new Date(updatedAt).toISOString())}
								</span>
							</>
						)}
					</span>
				</span>
			</button>
		</li>
	);
}

/** One plan in the active conversation. */
function PlanRow({
	plan,
	active,
	onSelect,
}: {
	plan: TestingSession;
	active: boolean;
	onSelect: () => void;
}) {
	return (
		<SidebarRow
			dot={plan.dot}
			title={plan.title}
			subtitle={plan.live ? "Running now" : statusLabel(plan.status)}
			subtitleAccent={plan.live}
			updatedAt={plan.updatedAt}
			active={active}
			onSelect={onSelect}
		/>
	);
}

/**
 * The RIGHT-pane History: one row per CONVERSATION (session), regardless of how many plans it holds.
 *
 * Replaces the old per-plan run history, which showed a single plan's runs and read as "1 plan" when
 * the user had five plans across two conversations. Session ≠ plan: this lists the conversations;
 * selecting one opens it and the left pane fills with its plans.
 */
export const TestingSessionHistory = memo(function TestingSessionHistory({
	sessions,
	activeScopeKey,
	loading,
	onSelect,
}: {
	sessions: TestingSessionGroup[];
	activeScopeKey: string | null;
	loading: boolean;
	onSelect: (session: TestingSessionGroup) => void;
}) {
	// Date headers + an "Active now" bucket, identical to how the plan list used to group — a flat list
	// of similar rows gives the eye nothing to anchor on when hunting for an earlier session.
	const groups = useMemo(() => bucketByRecency(sessions), [sessions]);

	if (loading && sessions.length === 0) {
		return (
			<div className="p-2">
				<PanelShimmer rows={4} compact />
			</div>
		);
	}

	if (sessions.length === 0) {
		return (
			<div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
				<MessagesSquare className="h-6 w-6 text-muted-foreground/40" />
				<p className="text-[11px] leading-relaxed text-muted-foreground">
					No sessions yet. Start a test in the chat and it shows up here.
				</p>
			</div>
		);
	}

	return (
		<div className="py-1">
			{groups.map((group) => (
				<section key={group.key} aria-label={group.label}>
					<h3 className="tst-micro sticky top-0 z-[1] bg-[var(--tst-panel-bg)] px-2.5 py-1.5">
						{group.label}
					</h3>
					<ul className="flex flex-col">
						{group.items.map((session) => (
							<SessionRow
								key={session.scopeKey}
								session={session}
								active={session.scopeKey === activeScopeKey}
								onSelect={() => onSelect(session)}
							/>
						))}
					</ul>
				</section>
			))}
		</div>
	);
});

/** One conversation in the History pane. */
function SessionRow({
	session,
	active,
	onSelect,
}: {
	session: TestingSessionGroup;
	active: boolean;
	onSelect: () => void;
}) {
	const subtitle =
		session.kind === "draft"
			? `Draft · ${session.messageCount} message${session.messageCount === 1 ? "" : "s"}`
			: session.live
				? "Running now"
				: `${session.planCount} plan${session.planCount === 1 ? "" : "s"}`;
	return (
		<SidebarRow
			dot={session.dot}
			title={session.title}
			subtitle={subtitle}
			subtitleAccent={session.live}
			updatedAt={session.updatedAt}
			active={active}
			onSelect={onSelect}
		/>
	);
}

function statusLabel(status: string): string {
	if (status === "passed") return "Passed";
	if (status === "failed") return "Failed";
	if (status === "approved") return "Approved";
	if (status === "proposed") return "Awaiting approval";
	if (status === "rejected") return "Rejected";
	if (status === "edited") return "Edited";
	return "Not run";
}

/**
 * Shared shape for the footer's three entry points, so they read as one group.
 *
 * forwardRef is required, not decorative: these are passed to Radix's `<DialogTrigger asChild>`,
 * which clones the child and attaches a ref to it. A plain function component drops that ref, and
 * Radix then cannot restore focus to the trigger when the dialog closes.
 */
const FooterAction = forwardRef<
	HTMLButtonElement,
	{ icon: React.ReactNode; label: string } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(function FooterAction({ icon, label, ...rest }, ref) {
	return (
		<button
			type="button"
			ref={ref}
			{...rest}
			className="tst-interactive flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
		>
			{icon}
			{label}
		</button>
	);
});

/**
 * The collapsed rail. Keeps the identity of the panel and whether something is running — so collapsing
 * is not the same as hiding. `live` is computed across all sessions by the caller.
 */
export const TestingSessionRail = memo(function TestingSessionRail({
	live,
	onExpand,
	onNew,
}: {
	live: boolean;
	onExpand: () => void;
	onNew: () => void;
}) {
	return (
		<div className="tst-panel flex h-full w-11 shrink-0 flex-col items-center gap-1 border-r py-1.5">
			<button
				type="button"
				onClick={onExpand}
				className="tst-interactive rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
				title="Expand plans (⌘B)"
				aria-label="Expand plans"
			>
				<PanelLeftOpen className="h-4 w-4" />
			</button>
			<button
				type="button"
				onClick={onNew}
				className="tst-interactive rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
				title="Start a new test (⌘⇧N)"
				aria-label="Start a new test"
			>
				<Plus className="h-4 w-4" />
			</button>
			{live && (
				<span className="mt-1" title="A test is running">
					<RunStatusDot status="busy" />
				</span>
			)}
		</div>
	);
});
